<template>
  <UIPopup
    v-model="modelValue"
    :closeable="false"
    :max-width="isDesktop ? (!currentStep ? '1130px' : '768px') : isTablet ? '553px' : ''"
    :hide-buttons="currentStep - 1 === steps.length"
  >
    <template #header>
      <div v-if="currentStep" class="mx-auto mt-14 flex max-w-[488px] items-center justify-center">
        <Transition name="fade">
          <UIButton
            v-if="currentStep > 1 && currentStep <= steps.length"
            id="back"
            variant="ghost"
            left-icon="arrow-big-filled-left"
            class="!absolute left-4 top-4 fill-gold-primary-20 !text-black-100 tablet:left-10 tablet:top-10"
            @click="currentStep--"
          >
            {{ $t('common.back') }}
          </UIButton>
        </Transition>
        <Transition name="fade" mode="out-in">
          <div v-if="currentStep <= steps.length" class="w-full px-3 py-2 desktop:p-4">
            <div class="flex w-full items-center gap-2 rounded-full bg-bg p-2 tablet:mt-16 tablet:p-2">
              <div
                v-for="step in steps.length"
                :key="step"
                class="h-4 rounded-full transition-colors duration-200"
                :style="{ width: `calc(100% / ${steps.length})` }"
                :class="
                  currentStep > step ? 'bg-gold-primary-100' : currentStep < step ? 'bg-bg' : 'bg-gold-primary-50'
                "
              ></div>
            </div>
            <h1 class="mt-8 text-center text-2xl text-black-100">{{ steps[currentStep - 1]?.title }}</h1>
          </div>
          <div v-else class="mx-auto flex max-w-[416px] flex-col items-center justify-center gap-5">
            <UIIcon name="check-filled" class="!size-[64px] text-gold-primary-100" size="unset" />
            <h1 class="text-center text-2xl text-black-100">{{ $t('onboarding.fillProfile.completion.title') }}</h1>
          </div>
        </Transition>
      </div>
    </template>
    <template #body>
      <div class="mx-auto mt-4" :class="{ 'max-w-[488px]': currentStep }">
        <div v-if="!currentStep" class="flex flex-col gap-7 text-center tablet:px-20">
          <div class="flex flex-col gap-4 tablet:flex-row">
            <div
              class="flex flex-col items-center justify-center gap-2.5 rounded-3xl bg-gold-primary-20 px-8 py-16 tablet:w-5/12"
            >
              <img src="/images/bridge.png" alt="bridge" class="size-12" />
              <h3 class="text-2xl">{{ $t('onboarding.fillProfile.welcome.title') }}</h3>
              <p class="text-black-80">
                <b>{{ $t('onboarding.fillProfile.welcome.subtitle') }}</b>
                {{ $t('onboarding.fillProfile.welcome.description') }}
              </p>
            </div>
            <div
              :key="currentStep"
              class="rounded-3xl border border-solid border-gold-primary-100 p-8 tablet:w-7/12"
              style="box-shadow: 0px 14px 28px 0px rgba(71, 71, 71, 0.15)"
            >
              <ul class="space-y-4">
                <li v-for="(item, index) in benefits" :key="index" class="flex items-start space-x-4">
                  <UIIcon name="check-filled-black" class="text-gold-primary-20" />
                  <div class="w-11/12 text-left">
                    <h3 class="font-bold text-black-80">{{ $t(item.title) }}</h3>
                    <p class="text-black-60">{{ $t(item.description) }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <p>
            {{ $t('onboarding.fillProfile.welcome.footer') }}
            <b>{{ $t('onboarding.fillProfile.welcome.welcomeAboard') }}</b>
            {{ $t('onboarding.fillProfile.welcome.finalNote') }}
          </p>
        </div>
        <div v-else-if="currentStep <= steps.length" class="flex flex-col gap-4 font-semibold">
          <span class="text-base">{{ $t(steps[currentStep - 1].questionKey) }}</span>
          <UIBaseTransition>
            <form :key="currentStep" ref="parentRef" class="flex w-full flex-col gap-4">
              <UITextArea
                v-if="!steps[currentStep - 1].options"
                v-model="steps[currentStep - 1].answer"
                name="answer"
                :placeholder="$t('onboarding.fillProfile.form.placeholder')"
                min-height="100"
                controlled="false"
              />
              <UIInputOptions
                v-else
                name="answer"
                :items="steps[currentStep - 1].options"
                sync-v-model
                controlled="false"
                class="mb-6"
              />
            </form>
          </UIBaseTransition>
        </div>
        <div v-else class="flex flex-col items-center justify-center gap-8">
          <p>{{ $t('onboarding.fillProfile.completion.subtitle') }}</p>
          <div class="flex flex-col gap-4">
            <UIButton id="book" class="w-full" @click="finish">
              {{ $t('onboarding.fillProfile.completion.bookButton') }}
            </UIButton>
            <p>{{ $t('onboarding.fillProfile.completion.footer') }}</p>
          </div>
        </div>
      </div>
    </template>
    <template #buttons>
      <div class="px-3 py-2 desktop:p-4 w-full">
        <UIButton
          v-if="currentStep <= steps.length"
          id="next"
          class="mx-auto w-full max-w-[488px]"
          @click="validateAnswer"
        >
          {{ $t('onboarding.fillProfile.form.next') }}
        </UIButton>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate'
import { POPUPS } from '../dynamic/maps'
import type { QA } from '@/types'
import { questionnaireSchema } from '@/schemas/profile/questionnaire'

const modelValue = defineModel<boolean>()

const uiStore = useUIStore()

const { isTablet, isDesktop } = useAppBreakpoints()

const currentStep = ref(0)

const { validateField, values } = useForm<QA>({
  validationSchema: questionnaireSchema,
})

const benefits = [
  {
    title: 'onboarding.fillProfile.benefits.deals.title',
    description: 'onboarding.fillProfile.benefits.deals.description',
  },
  {
    title: 'onboarding.fillProfile.benefits.insights.title',
    description: 'onboarding.fillProfile.benefits.insights.description',
  },
  {
    title: 'onboarding.fillProfile.benefits.consulate.title',
    description: 'onboarding.fillProfile.benefits.consulate.description',
  },
  {
    title: 'onboarding.fillProfile.benefits.discounts.title',
    description: 'onboarding.fillProfile.benefits.discounts.description',
  },
  {
    title: 'onboarding.fillProfile.benefits.events.title',
    description: 'onboarding.fillProfile.benefits.events.description',
  },
]

const steps = ref<QA[]>([
  {
    questionKey: 'onboarding.fillProfile.questions.interest',
    answer: '',
  },
  {
    questionKey: 'onboarding.fillProfile.questions.experience',
    answer: '',
  },
  {
    questionKey: 'onboarding.fillProfile.questions.partners',
    answer: '',
  },
  {
    questionKey: 'onboarding.fillProfile.questions.budget',
    answer: '',
    options: [
      {
        text: $t('onboarding.fillProfile.questions.budgetOptions.below100k'),
        value: 'Below $ 100K USD',
      },
      {
        text: $t('onboarding.fillProfile.questions.budgetOptions.100kTo500k'),
        value: '$ 100K - $ 500K USD',
      },
      {
        text: $t('onboarding.fillProfile.questions.budgetOptions.500kTo2m'),
        value: '$ 500K - $ 2M USD',
      },
      {
        text: $t('onboarding.fillProfile.questions.budgetOptions.2mTo5m'),
        value: '$ 2M - 5M USD',
      },
      {
        text: $t('onboarding.fillProfile.questions.budgetOptions.above5m'),
        value: 'Above $ 5M USD',
      },
    ],
  },
])

const { mutateAsync: saveAnswers } = useSaveQuestionnaireAnswers()

const { catchErrors } = useErrorHandler()

const validateAnswer = async () => {
  if (!currentStep.value) {
    currentStep.value++
    return
  }

  steps.value[currentStep.value - 1].answer = values.answer
  const { valid } = await validateField('answer')
  if (valid) {
    currentStep.value++
  }
}

watch(currentStep, async (value) => {
  if (value - 1 === steps.value.length) {
    await saveAnswers(
      steps.value.map((step) => ({
        questionKey: $t(step.questionKey),
        answer: step.answer,
      }))
    ).catch(catchErrors)
  }
})

const finish = () => {
  uiStore.showPopup(POPUPS.BOOK_A_CALL)
}
</script>
